import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { Copy, FileUp, MessageCircle, Plus, Trash2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { del, get, patch, post, put } from '../lib/api.ts'
import { fmtDate } from '../lib/format.ts'
import { useCondo } from '../lib/hooks.ts'
import { ROLE_OPTIONS, type Role } from '../lib/types.ts'
import { Badge, Button, Dialog, Empty, ErrorNote, Field, Input, PageHeader, Select, Sheet, Spinner } from '../components/ui.tsx'
import { ImportUnitsDialog } from './import-units.tsx'
import { t } from '../lib/i18n.ts'

interface Member { id: string; name: string; role: Role; roleLabel: string; title: string | null; since: string; phone?: string }
interface Invite { id: string; role: Role; roleLabel: string; nameHint: string | null; phone: string | null; unitLabel: string | null; createdAt: string; expiresAt: string; status: string }
interface Unit { id: string; label: string; block: string | null; kind: string; fracaoIdeal: string | null; mine: boolean; myRelation: string | null; delinquent?: boolean; notes?: string | null; people?: { membershipId: string; name: string; relation: string }[] }
const RELATION_LABEL: Record<string, string> = { proprietario: t('proprietário(a)'), inquilino: 'inquilino(a)', morador: 'morador(a)' }

export function Members() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { condoId, can, info } = useCondo(slug)
  const qc = useQueryClient()
  const [inviting, setInviting] = useState(false)
  const members = useQuery({ queryKey: ['members', condoId], queryFn: () => get<{ members: Member[] }>(`/api/condos/${condoId}/members`), enabled: Boolean(condoId) })
  const manage = can('members.manage')
  const invites = useQuery({ queryKey: ['invites', condoId], queryFn: () => get<{ invites: Invite[] }>(`/api/condos/${condoId}/invites`), enabled: Boolean(condoId && manage) })
  const setRole = useMutation({
    mutationFn: (a: { id: string; role: Role }) => patch(`/api/condos/${condoId}/members/${a.id}`, { role: a.role }),
    onSuccess: () => { toast.success(t('Papel alterado')); qc.invalidateQueries({ queryKey: ['members', condoId] }) },
    onError: (e) => toast.error((e as Error).message),
  })
  const remove = useMutation({
    mutationFn: (id: string) => del(`/api/condos/${condoId}/members/${id}`),
    onSuccess: () => { toast.success(t('Membro removido')); qc.invalidateQueries({ queryKey: ['members', condoId] }) },
    onError: (e) => toast.error((e as Error).message),
  })
  const revoke = useMutation({ mutationFn: (id: string) => del(`/api/condos/${condoId}/invites/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', condoId] }) })
  const myRole = info.data?.me.role
  return (
    <div>
      <PageHeader title={t('Membros e convites')}>
        {manage && <Button onClick={() => setInviting(true)}><UserPlus className="size-5" />{' '}{t('Convidar')}</Button>}
      </PageHeader>
      {members.isLoading ? <Spinner /> : (
        <Sheet>
          {members.data!.members.map((mm) => (
            <div key={mm.id} className="flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{mm.name}{mm.id === info.data?.me.membershipId && <span className="ml-2 text-sm font-normal text-tinta-2">{t('(você)')}</span>}</p>
                <p className="text-sm text-tinta-2">{mm.phone ? `${mm.phone}, ` : ''}{t('desde')}{' '}{fmtDate(mm.since)}</p>
              </div>
              {manage && mm.id !== info.data?.me.membershipId ? (
                <>
                  <Select aria-label={t('Papel de {name}', { name: mm.name })} className="w-auto min-w-44" value={mm.role} onChange={(e) => setRole.mutate({ id: mm.id, role: e.target.value as Role })}>
                    {ROLE_OPTIONS.filter(([r]) => r !== 'sindico' || myRole === 'sindico' || mm.role === 'sindico').map(([r, l]) => <option key={r} value={r}>{l}</option>)}
                  </Select>
                  <button className="rounded p-2 text-tinta-3 hover:text-margem" aria-label={t('Remover {name}', { name: mm.name })} onClick={() => confirm(t('Remover {name} do condomínio?', { name: mm.name })) && remove.mutate(mm.id)}><Trash2 className="size-5" /></button>
                </>
              ) : (
                <Badge tone={mm.role === 'sindico' || mm.role === 'subsindico' ? 'caneta' : 'neutral'}>{t(mm.roleLabel)}</Badge>
              )}
            </div>
          ))}
        </Sheet>
      )}
      {manage && (
        <section className="mt-8">
          <h2 className="mb-2 text-xl font-bold">{t('Convites')}</h2>
          {invites.data?.invites.length === 0 ? <p className="text-tinta-2">{t('Nenhum convite enviado ainda.')}</p> : (
            <Sheet>
              {invites.data?.invites.map((i) => (
                <div key={i.id} className="flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{i.nameHint ?? t('Convite')} <span className="font-normal text-tinta-2">— {t(i.roleLabel)}{i.unitLabel ? t(', unidade {unit}', { unit: i.unitLabel }) : ''}</span></p>
                    <p className="text-sm text-tinta-2">{t('Criado em')}{' '}{fmtDate(i.createdAt)}{t(', vale até')}{' '}{fmtDate(i.expiresAt)}</p>
                  </div>
                  <Badge tone={i.status === 'aceito' ? 'carimbo' : i.status === 'pendente' ? 'alerta' : 'neutral'}>{i.status}</Badge>
                  {i.status === 'pendente' && <Button size="sm" variant="quiet" onClick={() => revoke.mutate(i.id)}>{t('Revogar')}</Button>}
                </div>
              ))}
            </Sheet>
          )}
        </section>
      )}
      {inviting && <InviteDialog condoId={condoId} myRole={myRole} onClose={() => setInviting(false)} />}
    </div>
  )
}

function InviteDialog({ condoId, myRole, onClose }: { condoId: string; myRole?: Role; onClose: () => void }) {
  const qc = useQueryClient()
  const [f, setF] = useState({ nameHint: '', phone: '', role: 'condomino' as Role, unitId: '', relation: 'proprietario' })
  const [result, setResult] = useState<{ url: string; shareText: string; whatsappShareUrl: string } | null>(null)
  const units = useQuery({ queryKey: ['units', condoId], queryFn: () => get<{ units: Unit[] }>(`/api/condos/${condoId}/units`) })
  const m = useMutation({
    mutationFn: () => post<{ url: string; shareText: string; whatsappShareUrl: string }>(`/api/condos/${condoId}/invites`, {
      role: f.role, nameHint: f.nameHint || null, phone: f.phone || null, unitId: f.unitId || null, relation: f.unitId ? f.relation : null,
    }),
    onSuccess: (r) => { setResult(r); qc.invalidateQueries({ queryKey: ['invites', condoId] }) },
  })
  return (
    <Dialog open onClose={onClose} title={result ? t('Convite criado') : t('Convidar pessoa')}>
      {result ? (
        <div className="flex flex-col gap-4">
          <p>{t('Envie o link para a pessoa. Ele vale por 7 dias e só pode ser usado uma vez.')}</p>
          <p className="break-all rounded-ficha border border-pauta bg-papel px-3 py-2 font-doc text-[0.95rem]">{result.url}</p>
          <div className="flex flex-wrap gap-2">
            <a href={result.whatsappShareUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 rounded-ficha bg-carimbo px-4 font-semibold text-white"><MessageCircle className="size-5" />{' '}{t('Enviar pelo WhatsApp')}</a>
            <Button variant="outline" onClick={() => { void navigator.clipboard.writeText(result.shareText); toast.success(t('Mensagem copiada')) }}><Copy className="size-5" />{' '}{t('Copiar mensagem')}</Button>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="quiet" onClick={() => { setResult(null); setF({ ...f, nameHint: '', phone: '' }) }}>{t('Convidar outra pessoa')}</Button>
            <Button onClick={onClose}>{t('Concluir')}</Button>
          </div>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
          <Field label={t('Nome')} htmlFor="iv-n"><Input id="iv-n" value={f.nameHint} onChange={(e) => setF({ ...f, nameHint: e.target.value })} maxLength={120} placeholder={t('Ex.: Maria do 302')} /></Field>
          <Field label={t('WhatsApp (opcional)')} htmlFor="iv-p" hint={t('Se informado, só esse número consegue usar o convite.')}>
            <Input id="iv-p" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(31) 99999-0000" />
          </Field>
          <Field label={t('Papel no condomínio')} htmlFor="iv-r">
            <Select id="iv-r" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as Role })}>
              {ROLE_OPTIONS.filter(([r]) => r !== 'sindico' || myRole === 'sindico').map(([r, l]) => <option key={r} value={r}>{l}</option>)}
            </Select>
          </Field>
          {(units.data?.units.length ?? 0) > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('Unidade')} htmlFor="iv-u">
                <Select id="iv-u" value={f.unitId} onChange={(e) => setF({ ...f, unitId: e.target.value })}>
                  <option value="">{t('Nenhuma')}</option>
                  {units.data!.units.map((u) => <option key={u.id} value={u.id}>{u.block ? `${u.block} — ` : ''}{u.label}</option>)}
                </Select>
              </Field>
              {f.unitId && (
                <Field label={t('Vínculo')} htmlFor="iv-rel">
                  <Select id="iv-rel" value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })}>
                    <option value="proprietario">{t('Proprietário(a)')}</option><option value="inquilino">{t('Inquilino(a)')}</option><option value="morador">{t('Morador(a)')}</option>
                  </Select>
                </Field>
              )}
            </div>
          )}
          <ErrorNote error={m.error} />
          <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={onClose}>{t('Cancelar')}</Button><Button type="submit" busy={m.isPending}>{t('Criar convite')}</Button></div>
        </form>
      )}
    </Dialog>
  )
}

function UnitPeopleDialog({ condoId, unit, onClose }: { condoId: string; unit: Unit; onClose: () => void }) {
  const qc = useQueryClient()
  const members = useQuery({ queryKey: ['members', condoId], queryFn: () => get<{ members: Member[] }>(`/api/condos/${condoId}/members`) })
  const [sel, setSel] = useState<Record<string, string>>(Object.fromEntries((unit.people ?? []).map((p) => [p.membershipId, p.relation])))
  const m = useMutation({
    mutationFn: () => put(`/api/condos/${condoId}/units/${unit.id}/people`, { people: Object.entries(sel).map(([membershipId, relation]) => ({ membershipId, relation })) }),
    onSuccess: () => { toast.success(t('Pessoas da unidade atualizadas')); qc.invalidateQueries({ queryKey: ['units', condoId] }); onClose() },
  })
  const toggle = (id: string, on: boolean) => setSel(on ? { ...sel, [id]: sel[id] ?? 'proprietario' } : Object.fromEntries(Object.entries(sel).filter(([k]) => k !== id)))
  return (
    <Dialog open onClose={onClose} title={t('Pessoas da unidade {unit}', { unit: `${unit.block ? `${unit.block} — ` : ''}${unit.label}` })}>
      <p className="mb-3 text-[0.95rem] text-tinta-2">{t('Proprietários votam nas assembleias por esta unidade. Inquilinos e moradores reservam espaços e recebem os avisos.')}</p>
      <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <div className="max-h-[50dvh] overflow-y-auto rounded-ficha border border-pauta px-3">
          {members.isLoading && <Spinner />}
          {members.data?.members.map((mm) => (
            <div key={mm.id} className="flex flex-wrap items-center gap-3 border-b border-pauta py-2 last:border-b-0">
              <label className="flex min-w-0 flex-1 items-center gap-2">
                <input type="checkbox" className="size-5 accent-caneta" checked={mm.id in sel} onChange={(e) => toggle(mm.id, e.target.checked)} />
                <span className="truncate">{mm.name} <span className="text-sm text-tinta-2">({t(mm.roleLabel)})</span></span>
              </label>
              {mm.id in sel && (
                <Select aria-label={t('Vínculo de {name}', { name: mm.name })} className="w-auto min-h-9" value={sel[mm.id]} onChange={(e) => setSel({ ...sel, [mm.id]: e.target.value })}>
                  <option value="proprietario">{t('Proprietário(a)')}</option><option value="inquilino">{t('Inquilino(a)')}</option><option value="morador">{t('Morador(a)')}</option>
                </Select>
              )}
            </div>
          ))}
        </div>
        <ErrorNote error={m.error} />
        <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={onClose}>{t('Cancelar')}</Button><Button type="submit" busy={m.isPending}>{t('Salvar')}</Button></div>
      </form>
    </Dialog>
  )
}

function expandUnits(spec: string): string[] {
  const out: string[] = []
  for (const part of spec.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean)) {
    const m = /^(\d+)\s*-\s*(\d+)$/.exec(part)
    if (m) {
      const [a, b] = [Number(m[1]), Number(m[2])]
      for (let i = Math.min(a, b); i <= Math.max(a, b) && out.length < 500; i++) out.push(String(i))
    } else out.push(part.slice(0, 40))
  }
  return [...new Set(out)]
}

export function Units() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { condoId, can } = useCondo(slug)
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const [spec, setSpec] = useState('')
  const [block, setBlock] = useState('')
  const [peopleOf, setPeopleOf] = useState<Unit | null>(null)
  const units = useQuery({ queryKey: ['units', condoId], queryFn: () => get<{ units: Unit[] }>(`/api/condos/${condoId}/units`), enabled: Boolean(condoId) })
  const add = useMutation({
    mutationFn: () => post(`/api/condos/${condoId}/units`, { bulk: expandUnits(spec).map((label) => ({ label, block: block || null })) }),
    onSuccess: () => { toast.success(t('Unidades cadastradas')); setSpec(''); setAdding(false); qc.invalidateQueries({ queryKey: ['units', condoId] }) },
  })
  const upd = useMutation({ mutationFn: (a: { id: string; b: Record<string, unknown> }) => patch(`/api/condos/${condoId}/units/${a.id}`, a.b), onSuccess: () => qc.invalidateQueries({ queryKey: ['units', condoId] }) })
  const rm = useMutation({ mutationFn: (id: string) => del(`/api/condos/${condoId}/units/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['units', condoId] }) })
  const manage = can('units.manage')
  const preview = expandUnits(spec)
  return (
    <div>
      <PageHeader title={t('Unidades')}>
        {manage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setImporting(true)}><FileUp className="size-5" />{' '}{t('Importar lista')}</Button>
            <Button onClick={() => setAdding(true)}><Plus className="size-5" />{' '}{t('Cadastrar unidades')}</Button>
          </div>
        )}
      </PageHeader>
      {units.isLoading ? <Spinner /> : units.data!.units.length === 0 ? (
        <Empty title={t('Nenhuma unidade cadastrada')} action={manage ? <div className="flex flex-wrap justify-center gap-2"><Button variant="outline" onClick={() => setImporting(true)}><FileUp className="size-5" />{' '}{t('Importar a lista da administradora')}</Button><Button onClick={() => setAdding(true)}><Plus className="size-5" />{' '}{t('Cadastrar unidades')}</Button></div> : undefined}>
          {t('As unidades servem para os convites, as votações em assembleia e as reservas. Com a planilha ou o PDF da administradora, a importação cria as unidades e os convites de uma vez.')}
        </Empty>
      ) : (
        <Sheet>
          {units.data!.units.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-3 border-b border-pauta px-4 py-3 last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{u.block ? `${u.block} — ` : ''}{u.label} {u.mine && <Badge tone="caneta" className="ml-1">{t('Sua unidade')}{u.myRelation ? `, ${RELATION_LABEL[u.myRelation] ?? u.myRelation}` : ''}</Badge>}</p>
                {u.people && <p className="text-sm text-tinta-2">{u.people.length ? u.people.map((p) => `${p.name} (${RELATION_LABEL[p.relation] ?? p.relation})`).join(', ') : t('Ninguém vinculado')}</p>}
              </div>
              {manage && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setPeopleOf(u)}>{t('Pessoas')}</Button>
                  <label className="flex items-center gap-2 text-sm">
                    {t('Fração ideal')}
                    <Input className="w-28" inputMode="decimal" defaultValue={u.fracaoIdeal ?? ''} placeholder="0,0833" onBlur={(e) => { const v = Number(e.target.value.replace(',', '.')); if (e.target.value === '' || (v > 0 && v <= 1)) upd.mutate({ id: u.id, b: { fracaoIdeal: e.target.value === '' ? null : v } }) }} />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="size-5 accent-alerta" checked={Boolean(u.delinquent)} onChange={(e) => upd.mutate({ id: u.id, b: { delinquent: e.target.checked } })} />
                    {t('Inadimplente')}
                  </label>
                  <button aria-label={t('Excluir unidade {unit}', { unit: u.label })} className="rounded p-2 text-tinta-3 hover:text-margem" onClick={() => confirm(t('Excluir a unidade {unit}?', { unit: u.label })) && rm.mutate(u.id)}><Trash2 className="size-5" /></button>
                </>
              )}
            </div>
          ))}
        </Sheet>
      )}
      {manage && <p className="mt-3 text-sm text-tinta-2">{t('Vincule cada membro à sua unidade em “Pessoas”: proprietários votam nas assembleias por ela. A marcação de inadimplência é vista só pelo síndico e pelo conselho e aparece como aviso nas votações (Código Civil, art. 1.335, III).')}</p>}
      {peopleOf && <UnitPeopleDialog condoId={condoId} unit={peopleOf} onClose={() => setPeopleOf(null)} />}
      {importing && <ImportUnitsDialog condoId={condoId} onClose={() => setImporting(false)} />}
      <Dialog open={adding} onClose={() => setAdding(false)} title={t('Cadastrar unidades')}>
        <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); add.mutate() }}>
          <Field label={t('Unidades')} htmlFor="un-spec" hint={t('Separe por vírgula. Use intervalos: 101-104, 201-204, Cobertura.')}>
            <Input id="un-spec" value={spec} onChange={(e) => setSpec(e.target.value)} required placeholder="101-104, 201-204" />
          </Field>
          <Field label={t('Bloco ou torre (opcional)')} htmlFor="un-b"><Input id="un-b" value={block} onChange={(e) => setBlock(e.target.value)} maxLength={40} /></Field>
          {preview.length > 0 && <p className="text-sm text-tinta-2">{preview.length}{' '}{t('unidade(s):')}{' '}{preview.slice(0, 20).join(', ')}{preview.length > 20 ? '…' : ''}</p>}
          <ErrorNote error={add.error} />
          <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={() => setAdding(false)}>{t('Cancelar')}</Button><Button type="submit" busy={add.isPending}>{t('Cadastrar')}</Button></div>
        </form>
      </Dialog>
    </div>
  )
}
